import { use, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import classNames from 'classnames';

import { DatePicker, DatePickerMode, Dropdown } from '@jsmdg/yoshi';
import { type Location } from '../../shared/types';
import {
    buildAvailabilityCalendarViewModel,
    getDateKey,
    getInitialVisibleMonth,
} from '../components/AvailabilitiesContainer/availabilityCalendar';
import {
    AvailabilityDay,
    HiddenDay,
} from '../components/AvailabilitiesContainer/AvailabilityCalendarDay';
import { AvailabilityDayButtonContext } from '../contexts';
import { type Availability } from '../types/availability';
import styles from '../components/AvailabilitiesContainer/AvailabilitiesContainer.module.scss';

const messages = defineMessages({
    emptyState: {
        defaultMessage: 'Aktuell sind keine Termine verfügbar.',
    },
    selection: {
        defaultMessage: 'Kalenderansicht mit Monatsnavigation ohne Datumsauswahl',
    },
    locationLabel: {
        defaultMessage: 'Standort wählen',
    },
    locationHelper: {
        defaultMessage: 'Bitte wähle einen Standort, um die Verfügbarkeit zu sehen.',
    },
});

type AvailabilityCalendarProviderProps = {
    readonly availabilitiesPromise: Promise<Availability[]> | null;
    readonly bookableLocations: Location[];
    readonly formatAppointmentCount: (count: number) => string;
    readonly locationOptions?: Array<{
        readonly label: string;
        readonly value: string;
    }>;
    readonly onLocationChange?: (locationId: string) => void;
    readonly selectedLocation: string;
};

const AvailabilityCalendarProvider = ({
    availabilitiesPromise,
    bookableLocations,
    formatAppointmentCount,
    locationOptions = [],
    onLocationChange = () => {},
    selectedLocation,
}: AvailabilityCalendarProviderProps): JSX.Element => {
    const intl = useIntl();
    const availabilities = availabilitiesPromise ? use(availabilitiesPromise) : [];
    const [visibleMonth, setVisibleMonth] = useState(() => getInitialVisibleMonth(availabilities));
    const calendarViewModel = buildAvailabilityCalendarViewModel(visibleMonth, availabilities);

    const availabilityDayButtonContextValue = {
        availabilityCountByDate: new Map(
            calendarViewModel.days.map(day => [getDateKey(day.date), day.amount]),
        ),
        formatAppointmentCount,
    };

    return (
        <AvailabilityDayButtonContext value={availabilityDayButtonContextValue}>
            {Boolean(selectedLocation) && !availabilities.length ? (
                <p className={styles.calendarState}>{intl.formatMessage(messages.emptyState)}</p>
            ) : null}
            <div
                className={classNames(
                    styles.datePicker,
                    !selectedLocation && styles.datePickerWithoutSelection,
                    'position-relative',
                )}
            >
                <DatePicker
                    className="pt-md-3x pt-2x px-2x px-md-3x pb-2x"
                    animate
                    captionLayout="label"
                    headerStart={
                        bookableLocations.length > 1 ? (
                            <div className="d-flex flex-column gap-1-5x">
                                <p className={styles.headerStartHint}>
                                    {intl.formatMessage(messages.locationHelper)}
                                </p>
                                <Dropdown
                                    ariaLabel={intl.formatMessage(messages.locationLabel)}
                                    className={styles.headerDropdown}
                                    label={intl.formatMessage(messages.locationLabel)}
                                    onChange={onLocationChange}
                                    options={locationOptions}
                                    value={selectedLocation}
                                />
                            </div>
                        ) : undefined
                    }
                    mode={DatePickerMode.Single}
                    onChange={() => {}}
                    showOutsideDays
                    value={undefined}
                    withBorder={false}
                    a11yMessages={{
                        selectDate: intl.formatMessage(messages.selection),
                        selection: intl.formatMessage(messages.selection),
                    }}
                    dayPickerProps={{
                        components: {
                            Day: HiddenDay,
                            DayButton: AvailabilityDay,
                        },
                        fixedWeeks: true,
                        month: calendarViewModel.visibleMonth,
                        onMonthChange: setVisibleMonth,
                        startMonth: calendarViewModel.startMonth,
                        endMonth: calendarViewModel.endMonth,
                    }}
                />
            </div>
        </AvailabilityDayButtonContext>
    );
};

export { AvailabilityCalendarProvider };
